import type {
  LocationMessageRequestBody,
  LocationMessagesResponse,
  ReturnDataType,
  V3TokenResponse,
} from '../models/vessels/internal-ais-api-v3.model';
import type {
  VesselsRequestBody,
  VesselsResponse,
} from '../models/vessels/kpler-vessels.model';
import { wrapRequest } from './base';

/**
 * Fetch AIS position messages for vessels matching the supplied filters.
 *
 * Wraps `POST /location_messages_v2` via the backend proxy.
 *
 * **At least one vessel filter** (`vessel_uids`, `mmsis`, `imos`, `flags`,
 * `ship_types`, or `geometry`) must be provided in `body`, with the exception
 * of `most_recent` mode where a `start_datetime` alone is sufficient.
 *
 * Supply `return_data_type: 'json_grouped_by_vessel_uid'` to receive data keyed
 * by vessel UID; otherwise a flat array is returned.
 *
 * @example
 * // Time-range query, flat response
 * const result = await getLocationMessages({
 *   callerFeature: 'my-feature',
 *   body: {
 *     mmsis: [123456789],
 *     start_datetime: '2026-01-01T00:00:00Z',
 *     end_datetime: '2026-01-02T00:00:00Z',
 *     limit: 1000,
 *   },
 * });
 *
 * @example
 * // Most-recent query with geometry filter
 * const result = await getLocationMessages({
 *   callerFeature: 'my-feature',
 *   body: {
 *     geometry: 'POLYGON((...))',
 *     most_recent: true,
 *   },
 * });
 *
 * @example
 * // Paginated — pass the cursor from the previous response
 * const nextPage = await getLocationMessages({
 *   callerFeature: 'my-feature',
 *   body: {
 *     mmsis: [123456789],
 *     start_datetime: '2026-01-01T00:00:00Z',
 *     end_datetime: '2026-01-02T00:00:00Z',
 *     limit: 1000,
 *     cursor: result.cursor,
 *   },
 * });
 */
export const getLocationMessages = async <
  T extends ReturnDataType = 'json_flat',
>({
  callerFeature,
  body,
}: {
  callerFeature: string;
  body: LocationMessageRequestBody;
}): Promise<LocationMessagesResponse<T>> =>
  wrapRequest<LocationMessagesResponse<T>>('post', 'geonius', '/v2/ais-data', {
    headers: {
      'x-caller-product': 'Risk Intelligence',
      'x-caller-feature': callerFeature,
    },
    body,
    queryStringParameters: {
      upstream_path: 'location_messages_v2',
    },
  });

/**
 * Get a JWT token for AIS API v3 requests, via the backend proxy.
 *
 * These can only be used once each, don't try to use the same one several times - it won't work.
 *
 * @param callerFeature - Name of the calling feature, for logging and monitoring purposes.
 * @returns V3TokenResponse
 */
export const getV3Token = async ({
  callerFeature,
}: {
  callerFeature: string;
}): Promise<V3TokenResponse> =>
  wrapRequest<V3TokenResponse>('post', 'geonius', '/v2/ais-data', {
    headers: {
      'x-caller-product': 'Risk Intelligence',
      'x-caller-feature': callerFeature,
    },
    // force empty body so signing doesn't fail
    body: {},
    queryStringParameters: {
      upstream_path: 'token',
    },
  });

/**
 * Look up vessel dimension, tonnage, and design/superstructure data from the
 * AIS Data API's vessel advanced characteristics lookup.
 *
 * Wraps `POST /vessels_v2` via the backend proxy.
 *
 * **At least one identifier field** (`vessel_uids`, `mmsis`, or `imos`) must
 * be provided in `body`.
 *
 * Unlike the deprecated `/vessels` endpoint, there is no `exact` (MMSI+IMO
 * AND-match) filter and no `cursor`-based pagination — pagination is via
 * `page_number` instead. When the response contains a `page_number`, more
 * pages are available — pass it back as `body.page_number` in the next call
 * (keep all other parameters identical).
 *
 * ⚠️ Sort order (`sort_key` / `sort_direction`) is applied **within each page
 * only**. For a globally sorted result, collect all pages first and sort
 * client-side.
 *
 * @example
 * // Look up vessels by MMSI
 * const result = await getVessels({
 *   callerFeature: 'vessel-particulars',
 *   body: {
 *     mmsis: [123456789, 234567890],
 *     sort_key: 'mmsi',
 *     sort_direction: 'ascending',
 *   },
 * });
 *
 * @example
 * // Look up a vessel by IMO
 * const result = await getVessels({
 *   callerFeature: 'vessel-particulars',
 *   body: {
 *     imos: [1234567],
 *   },
 * });
 *
 * @example
 * // Paginated — pass the page_number from the previous response
 * const nextPage = await getVessels({
 *   callerFeature: 'vessel-particulars',
 *   body: {
 *     mmsis: [123456789],
 *     page_number: result.page_number,
 *   },
 * });
 */
export const getVessels = async ({
  callerFeature,
  body,
}: {
  callerFeature: string;
  body: VesselsRequestBody;
}): Promise<VesselsResponse> =>
  wrapRequest<VesselsResponse>('post', 'geonius', '/v2/ais-data', {
    headers: {
      'x-caller-product': 'Risk Intelligence',
      'x-caller-feature': callerFeature,
    },
    body,
    queryStringParameters: {
      upstream_path: 'vessels_v2',
    },
  });
